import { useId, type ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

export interface CardProps {
    title?: ReactNode;
    description?: ReactNode;
    /** What the card does, at the right of its header: a button, a toolbar. */
    action?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
    /** Drops the body's padding, for a table or a list that draws its own edges. */
    flush?: boolean;
    className?: string;
}

/**
 * Paper, a 1px line, radius 8, no shadow (docs/10 §4). A titled card is named
 * by its title and described by its description, so it is a region a screen
 * reader can reach by name — and the same name is how a spec finds it.
 */
export function Card({ title, description, action, footer, children, flush, className }: CardProps) {
    const id = useId();
    const titleId = title ? `${id}-title` : undefined;
    const descriptionId = description ? `${id}-description` : undefined;
    return (
        <section
            data-slot="card"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className={cn('flex flex-col rounded-card border border-border bg-card text-card-foreground', className)}
        >
            {(title || description || action) && (
                <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border px-6 py-5">
                    <div className="min-w-0">
                        {title && (
                            <h3 id={titleId} className="text-18 font-semibold tracking-heading">
                                {title}
                            </h3>
                        )}
                        {description && (
                            <p id={descriptionId} className="mt-1 text-13 text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </div>
                    {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
                </header>
            )}
            {children !== undefined && children !== null && <div className={cn(!flush && 'p-6')}>{children}</div>}
            {/* A card stretched to its row's height keeps the footer on its bottom edge. */}
            {footer && <footer className="mt-auto rounded-b-card border-t border-border bg-muted px-6 py-4">{footer}</footer>}
        </section>
    );
}
